import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance
} from 'vue'
import { useRouter } from 'vue-router/composables'
import { useAuth } from '@/composables/useAuth'
import {
  emitOpenLoginDialog,
  emitter,
  VARUNA_LOGIN_SYNCED
} from '@/plugins/emitter'
import LoginDialog from '@/components/LoginDialog.vue'
import ReferralCampaignHeadline from '@/components/yagiprime/ReferralCampaignHeadline.vue'
import YagiprimePaymentDialog from '@/components/yagiprime/YagiprimePaymentDialog.vue'
import {
  attachReferral,
  fetchReferralStatus
} from '@/services/yagiprime/referral-campaign-client'
import type { ReferralStatusResponse } from '@/services/yagiprime/referral-campaign-client'
import {
  clearReferralCode,
  loadReferralCode
} from '@/utils/yagiprime/referralVisitor'
import campaign from '@/utils/yagiprime/referralCampaign.json'

type BannerState = 'invite' | 'registered' | 'ineligible'

/**
 * authReady の解決を待つ上限。ここを無条件に待つと、Firebase の初期化を
 * 取りこぼした環境でバナーが永久に出ない（＝キャンペーンが黙って死ぬ）。
 * 待ちきれない場合はその時点の認証状態で出し分ける。
 */
type YagiprimePaymentService = {
  init: (vm: unknown) => void
  useFeature: (
    vm: unknown,
    featureType: string,
    options?: { onSuccess?: () => void }
  ) => unknown
}

/**
 * 出し分けが確定したことと、その結果このバナーを出すかどうかを親へ渡す。
 * 着地ページ(/r/<code>)はこれを待ってつなぎ表示を畳む。確定まで数秒〜
 * 数十秒（authReady 5秒 + API 15秒）かかるので、待たずに畳むと空白になる。
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'ReferralInviteeBanner',
  props: {
    showIneligible: { type: Boolean, default: true }
  },
  emits: ["settled"],
  setup(__props: any, { emit }: { emit: ({ (e: 'settled', visible: boolean): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { showIneligible: boolean };

/**
 * 招待された側がトップに着地したときのバナー（ワイヤー）。
 *
 * `/r/<code>` の着地でコードを localStorage に保存してトップへ replace するので、
 * 「保存済みのコードがある」＝紹介URL経由で来た、と判定できる。
 *
 * ⚠️ ログイン状態だけで「紹介が成立した」と判断してはいけない。
 * このバナーの CTA が開くダイアログは新規登録とログインを兼ねていて、
 * フロントはどちらも同じ経路で referral_code を送り、成功したら
 * clearReferralCode() する。つまり既存会員がログインしただけでも
 * 「コードが消えて認証済みになる」状態が同じように起きる。
 * ここで「30日間無料」と出すと、対象外の人に課金条件を誤案内することになる。
 *
 * 成立の正本はサーバー（referral_attribution）にしかないので、認証後は
 * status API の referee_pending_days を見て、0 より大きいときだけ特典を出す。
 *
 * ⚠️ 引き直すきっかけを isAuthenticated の変化にしてはいけない。Firebase の
 * 認証成功と varuna への登録 POST には時間差があり、認証直後に status を叩くと
 * referral_attribution がまだ無く 0 が返る（＝主対象に「対象外」と誤表示）。
 * POST 完了で飛ぶ VARUNA_LOGIN_SYNCED を待つ。
 *
 * 加入までこの画面のまま済ませる。ヤギプラ加入は
 * $yagiprimePaymentService.useFeature() でモーダルを開く（カードなら 3DS 込みで完結）。
 * ⚠️ useFeature に未ログインのまま入れてはいけない。内蔵フローは
 * 「ログインダイアログが閉じたら加入へ進む」で、上記の登録 POST を待たないため、
 * 紐付く前に加入して trial_end に30日が乗らない事故になる。
 */
const AUTH_WAIT_MS = 5000

/** 加入導線の識別子。route_id / コールバックの紐付けにだけ使う任意文字列 */
const FEATURE_TYPE = 'referral-invitee'

/** 加入後の行き先。加入状況が見える画面へ送る */
const AFTER_SUBSCRIBE_PATH = '/mypage/yagiprime'

const copy = campaign.invitee





const vm = getCurrentInstance()!.proxy as unknown as {
  $yagiprimePaymentService: YagiprimePaymentService
}
const router = useRouter()

const { user, isAuthenticated, whenAuthReady } = useAuth()

/** 紹介URL経由で来たか。localStorage は初期化順の都合で mounted 後に読む */
const hasReferral = ref(false)
/** 出し分けの準備ができたか。確定するまで何も出さない */
const settled = ref(false)
/** サーバーが返した、被紹介者として次の加入で無料になる日数。出し分けの判定に使う */
const refereePendingDays = ref(0)
/** 次の加入で実際に無料になる合計日数。加入モーダルの「N日間無料」表示に使う */
const pendingDaysTotal = ref(0)

const state = computed<BannerState>(() => {
  if (!isAuthenticated.value) return 'invite'
  return refereePendingDays.value > 0 ? 'registered' : 'ineligible'
})

// 認証と成立判定が確定する前に出すと、セッション復元中の既存会員へ
// 一瞬 invite（＝対象外の人への特典訴求）が出てしまう
const isVisible = computed(
  () =>
    campaign.isActive &&
    hasReferral.value &&
    settled.value &&
    (props.showIneligible || state.value !== 'ineligible')
)

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function onRegisterClick(): void {
  emitOpenLoginDialog()
}

/** ヤギプラ加入モーダルを開く。ログイン済みでしか呼ばない */
function openPrimeDialog(): void {
  vm.$yagiprimePaymentService.useFeature(vm, FEATURE_TYPE, {
    onSuccess: () => {
      router.push(AFTER_SUBSCRIBE_PATH).catch(() => {
        // 同一ルートへの遷移等で reject されても致命ではない
      })
    }
  })
}

function applyStatus(status: ReferralStatusResponse | null): void {
  refereePendingDays.value = status?.referee_pending_days || 0
  pendingDaysTotal.value = status?.pending_days_total || 0
}

/** 成立の正本はサーバー。取得できなければ 0 のまま＝特典を訴求しない */
async function syncRefereeState(): Promise<void> {
  const uid = user.value?.uid
  if (!uid) {
    applyStatus(null)
    return
  }
  applyStatus(await fetchReferralStatus(uid))
}

/**
 * ログイン済みのまま紹介URLで来た会員をその場で紐付ける。
 *
 * 紐付けは login の POST に載る referral_code で成立するが、すでに
 * ログインしているブラウザでは認証イベントが起きず login が飛ばない。
 * ここで叩かないと、既存ヤギッシュ会員（ヤギプラ未加入）が紹介URLから
 * 来ても「対象外」表示のままになる。
 *
 * 対象外の会員にはサーバーが紐付けを拒否して現状の status を返すので、
 * 表示の出し分けはこれまでどおりサーバーの値だけで決まる。
 */
async function attachIfAuthenticated(): Promise<boolean> {
  const uid = user.value?.uid
  const code = loadReferralCode()
  if (!uid || !code) return false
  // 紐付けは取り消せないので、サーバーは ID トークンで本人確認する。
  // 取れなかったら紐付けない（コードは消さないので次の機会に再試行できる）
  const idToken = await user.value.getIdToken().catch(() => '')
  if (!idToken) return false
  const status = await attachReferral(uid, code, idToken)
  if (!status) return false
  applyStatus(status)
  // 紐付いたコードは用済み。login 経路と同じく捨てて再送しない
  if (status.is_referee) clearReferralCode()
  return true
}

/**
 * ダイアログでの登録・ログインは mounted の後に起きるので、その完了で引き直す。
 * 紐付けが成立したときだけ、続けて加入モーダルまで開く（対象外の人には開かない）。
 */
async function onVarunaLoginSynced(): Promise<void> {
  await syncRefereeState()
  if (refereePendingDays.value > 0) openPrimeDialog()
}

onMounted(async () => {
  // 登録成功時に clearReferralCode() されるので、読むのは認証を待つ前
  hasReferral.value = !!loadReferralCode()
  await Promise.race([whenAuthReady(), delay(AUTH_WAIT_MS)])
  // 紐付けは status より先。後にすると初回描画が「対象外」になってから覆る
  const attached = await attachIfAuthenticated()
  if (!attached) await syncRefereeState()
  settled.value = true
  emit('settled', isVisible.value)
  // 決済結果の購読・PayPay/Stripe リダイレクト検知。加入モーダルを出す側の責務
  vm.$yagiprimePaymentService.init(vm)
  emitter.on(VARUNA_LOGIN_SYNCED, onVarunaLoginSynced)
})

onBeforeUnmount(() => {
  emitter.off(VARUNA_LOGIN_SYNCED, onVarunaLoginSynced)
})

return { __sfc: true,AUTH_WAIT_MS, FEATURE_TYPE, AFTER_SUBSCRIBE_PATH, copy, emit, props, vm, router, user, isAuthenticated, whenAuthReady, hasReferral, settled, refereePendingDays, pendingDaysTotal, state, isVisible, delay, onRegisterClick, openPrimeDialog, applyStatus, syncRefereeState, attachIfAuthenticated, onVarunaLoginSynced, LoginDialog, ReferralCampaignHeadline, YagiprimePaymentDialog, campaign }
}

})